import type { Dispatch, SetStateAction } from "react";
import { Fragment, memo } from "react";
import "Components/ConfigLayout/Camera/OverlaySettings/LogoMotionOverlaySettings/LogoMotionOverlaySettings.less";
import type { LogoDisplayModeType, LogoEventType } from "Interfaces";
import { LOGO_DISPLAY_MODE_TYPES, LOGO_EVENT_TYPES, type OverlayState } from "Interfaces";
import { produce } from "immer";
import {
	AspectMaintainer, Button, ButtonTypes, Checkbox, Input, PopoverPlacement, Select, type SelectOption, Switch
} from "@clintonelec/react-storybook";
import UploadModal from "../UploadModal";
import { addOrRemoveFromArray } from "@clintonelec/typescriptutils";

type LogoMotionOverlaySettingsProps = {
	settings: OverlayState;
	setOverlaySettings: Dispatch<SetStateAction<OverlayState>>;
};

const logoDisplayModeOptions: SelectOption[] = LOGO_DISPLAY_MODE_TYPES.map(type => ({
	label: `Show ${ type.charAt(0).toUpperCase() + type.slice(1) } on Event`,
	value: type
}));

function LogoMotionOverlaySettings({ settings, setOverlaySettings }: LogoMotionOverlaySettingsProps) {
	const { logoMotionOverlay, enableOverlays: mainToggle } = settings;
	const { enabled, file, displayMode, eventTypes } = logoMotionOverlay;
	const uploadRestrictions = `
			Please upload image files in .png or .jpg formats that are under 512KB.
			Files must be no larger than 1920px wide and up to 1080px tall.
		`;

	const validateFile = async (payload: File) => {
		if (payload.size > 512000) {
			return false;
		}

		const image = await createImageBitmap(payload);

		if (image.width > 1920 || image.height > 1080) {
			return false;
		}

		return true;
	};

	const handleEnableOverlayChange = (newEnableOverlay: boolean) => {
		setOverlaySettings(produce(settings, draft => {
			draft.logoMotionOverlay.enabled = newEnableOverlay;
		}));
	};

	const handleEventTypeChange = (type: LogoEventType) => () => {
		const updatedEventTypes = addOrRemoveFromArray<LogoEventType>(Array.from(eventTypes), type);

		setOverlaySettings(produce(settings, draft => {
			draft.logoMotionOverlay.eventTypes = new Set(updatedEventTypes);
		}));
	};

	const handleDisplayModeChange = (mode: string | null) => {
		if (mode) {
			setOverlaySettings(produce(settings, draft => {
				draft.logoMotionOverlay.displayMode = mode as LogoDisplayModeType;
			}));
		}
	};

	const handleFileUpdate = (value: string) => {
		setOverlaySettings(produce(settings, draft => {
			if (!value && file !== null) {
				draft.logoMotionOverlay.file = null;
			}
		}));
	};

	const uploadFile = (name: string, dataUrl: string) => {
		setOverlaySettings(produce(settings, draft => {
			draft.logoMotionOverlay.file = {
				name,
				dataUrl
			};
		}));
	};

	const renderOverlayImage = () => {
		if (!file) {
			return;
		}

		return (
			<img className="image" src={ file.dataUrl } />
		);
	};

	const renderSettingsContent = () => {
		if (!enabled) {
			return <div className="enable-message">Enable Logo Motion to adjust settings.</div>;
		}

		const eventWarning = eventTypes.size < 1
			? <div className="form-row event-type-warning inset">You must enable at least one event type</div>
			: null;

		return (
			<Fragment>
				<div className="form-row">
					<span>Logo Motion File</span>
					<div className="file-select-wrapper">
						<UploadModal
							uploadFile={ uploadFile }
							restrictions={ uploadRestrictions }
							validateFile={ validateFile }
						>
							<Button htmlType="button" type={ ButtonTypes.TERTIARY }>
								Browse
							</Button>
						</UploadModal>
						<Input
							disabled
							placeholder="Upload a file"
							value={ file?.name }
							clearable
							wrapClassName="file-select-input-wrapper"
							onUpdate={ handleFileUpdate }
						/>
					</div>
				</div>

				<div className="form-row">
					<span>Display Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						value={ displayMode }
						options={ logoDisplayModeOptions }
						onSelect={ handleDisplayModeChange }
					/>
				</div>

				<div className="form-row">
					<span>Event Type</span>
					<div className="event-types">
						{ LOGO_EVENT_TYPES.map(type => (
							<Checkbox
								checked={ eventTypes.has(type) }
								onChange={ handleEventTypeChange(type) }
								key={ type }
							>
								<span>{ type }</span>
							</Checkbox>
						)) }
					</div>
				</div>
				{ eventWarning }
			</Fragment>
		);
	};

	const renderLogoControls = () => {
		if (!mainToggle) {
			return <div className="enable-message">Enable Overlays to adjust settings.</div>;
		}

		return (
			<Fragment>
				<div className="form-row">
					<Switch
						className="enable-overlay-switch"
						checked={ enabled }
						onChange={ handleEnableOverlayChange }
					/>
					<span>Enable Logo Motion</span>
				</div>

				{ renderSettingsContent() }
			</Fragment>
		);
	};

	return (
		<div className="logo-overlay-settings">
			<div className="column">
				<AspectMaintainer>
					<div className="live-snapshot">
						<AspectMaintainer>
							<div className="preview-wrapper border">
								{ renderOverlayImage() }
								<div className="camera-snapshot" />
							</div>
						</AspectMaintainer>
					</div>
				</AspectMaintainer>
			</div>
			<div className="column">
				{ renderLogoControls() }
			</div>
		</div>
	);
}

export default memo(LogoMotionOverlaySettings);
